import React, { useEffect, useRef, useState } from 'react';

interface LazyVisibleProps {
    /** Pixel height reserved for the placeholder so scroll positions don't jump when children mount. */
    placeholderHeight: number;
    /** Pixel width reserved for the placeholder. Use when the layout doesn't constrain width (e.g. horizontal flex). */
    placeholderWidth?: number;
    /** Distance ahead of the viewport at which content starts mounting, in CSS pixels. */
    rootMargin?: string;
    /** Optional scroll container; defaults to the nearest scrolling ancestor (null root). */
    rootRef?: React.RefObject<Element | null>;
    /** Class applied to the wrapper element in both placeholder and rendered states. */
    className?: string;
    /** Tag of the wrapper element — defaults to `div`. */
    as?: keyof JSX.IntrinsicElements;
    children: React.ReactNode;
}

/**
 * Renders children only after the wrapper enters (or comes within `rootMargin`
 * of) the scroll viewport. Once mounted, children stay mounted — re-virtualising
 * on scroll-out would cost more (React reconciliation, image re-decode) than the
 * memory it saves for normal list sizes.
 *
 * Used by EventTilesList / EventItemsList to keep the DOM cost of a 2000-event
 * langenachtderkirchen response manageable without pulling in a virtualisation
 * library that would conflict with the existing grid / horizontal-flex / list
 * layouts in those components.
 */
export const LazyVisible: React.FC<LazyVisibleProps> = ({
    placeholderHeight,
    placeholderWidth,
    rootMargin = '600px',
    rootRef,
    className,
    as: Tag = 'div',
    children,
}) => {
    const ref = useRef<HTMLElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (visible) return;
        const el = ref.current;
        if (!el) return;

        // Skip the observer when IntersectionObserver isn't available (e.g.
        // very old environments or SSR shells). Render eagerly in that case.
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        observer.disconnect();
                        return;
                    }
                }
            },
            { root: rootRef?.current ?? null, rootMargin },
        );
        observer.observe(el);

        return () => observer.disconnect();
    }, [visible, rootRef, rootMargin]);

    const placeholderStyle: React.CSSProperties = visible
        ? {}
        : {
              minHeight: placeholderHeight,
              minWidth: placeholderWidth,
          };

    return (
        <Tag
            ref={ref as React.RefObject<any>}
            className={className}
            style={placeholderStyle}
        >
            {visible ? children : null}
        </Tag>
    );
};

export default LazyVisible;
